/**
 * Dedicated watch page for the product demo video.
 *
 * Exists for one reason: Google will not index a video unless it sits on a
 * *watch page* — a page "whose main purpose is to show users a single video."
 * The homepage plays the same recording, but it plays it seven sections down a
 * marketing page, so the Video indexing report returned "Video isn't on a watch
 * page" for `https://anter.ai/` and the video was never indexed. No amount of
 * markup on `/` changes that verdict; the video needs a page of its own where
 * it is the first, largest, and only thing above the fold.
 *
 * Everything here is in service of that classification, so be careful what gets
 * added:
 *  - The <video> is the first element inside <main> and fills the viewport
 *    width. Do not push it below a hero, a banner, or a nav promo.
 *  - Exactly one video on the page. A second player (or an expand-modal
 *    duplicate like the homepage's) splits prominence between two elements,
 *    which is one of the documented causes of this same error.
 *  - `poster` is required, not decorative — it is the `thumbnailUrl` Google
 *    renders in the video result, and a video with no reachable thumbnail is
 *    dropped even when everything else validates.
 *  - Title and description must stay unique to this video and must not be
 *    reused from the homepage.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Dot } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageSEO from "../components/PageSEO";
import SiteFooter from "../components/SiteFooter";
import SiteHeader from "../components/SiteHeader";
import {
  FLOW_EVENTS,
  VIDEO_DESCRIPTION,
  VIDEO_DURATION_ISO,
  VIDEO_HEIGHT,
  VIDEO_NAME,
  VIDEO_POSTER,
  VIDEO_UPLOAD_DATE,
  VIDEO_WATCH_PATH,
  VIDEO_WATCH_URL,
  VIDEO_WIDTH,
  YOUTUBE_EMBED_URL,
  formatClock,
} from "../content/demoVideo";

const BASE_URL = "https://anter.ai";
const WATCH_URL = `${BASE_URL}${VIDEO_WATCH_PATH}`;

/**
 * `VideoObject` lives only on this page. Adding it to the homepage as well
 * would advertise a second, non-watch-page location for the same video and
 * invite Google to keep flagging it — the watch page is the canonical home.
 *
 * `hasPart` Clips are what produce Key Moments beneath the search result. Each
 * clip's `url` carries a media fragment (`#t=`), which the effect below reads
 * so a Key Moment click lands at the right timestamp.
 */
const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "@id": `${WATCH_URL}#video`,
  name: VIDEO_NAME,
  description: VIDEO_DESCRIPTION,
  thumbnailUrl: [`${BASE_URL}${VIDEO_POSTER}`],
  uploadDate: VIDEO_UPLOAD_DATE,
  duration: VIDEO_DURATION_ISO,
  embedUrl: YOUTUBE_EMBED_URL,
  contentUrl: VIDEO_WATCH_URL,
  url: WATCH_URL,
  width: VIDEO_WIDTH,
  height: VIDEO_HEIGHT,
  inLanguage: "en-US",
  isFamilyFriendly: true,
  publisher: {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    name: "Anter",
    url: BASE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${BASE_URL}/favicon.svg`,
    },
  },
  hasPart: FLOW_EVENTS.map((event, index) => ({
    "@type": "Clip",
    name: event.title,
    startOffset: event.second,
    endOffset: FLOW_EVENTS[index + 1]?.second ?? 152,
    url: `${WATCH_URL}#t=${event.second}`,
  })),
  potentialAction: {
    "@type": "SeekToAction",
    target: `${WATCH_URL}#t={seek_to_second_number}`,
    "startOffset-input": "required name=seek_to_second_number",
  },
};

export default function WatchDemoPage() {
  const [currentSecond, setCurrentSecond] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  const activeEventIndex = useMemo(() => {
    let index = 0;
    for (let i = 0; i < FLOW_EVENTS.length; i += 1) {
      const event = FLOW_EVENTS[i];
      if (event && currentSecond >= event.second) index = i;
    }
    return index;
  }, [currentSecond]);

  function seekTo(second: number) {
    setCurrentSecond(second);
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Honour the `#t=<seconds>` media fragment that the SeekToAction and the
  // Clip URLs above hand to Google, so a Key Moment result opens at its moment
  // instead of at 00:00.
  useEffect(() => {
    const match = window.location.hash.match(/^#t=(\d+)/);
    if (!match) return;
    const second = Number(match[1]);
    if (Number.isFinite(second)) {
      setCurrentSecond(second);
    }
  }, []);

  return (
    <>
      <PageSEO
        title="Anter Product Demo — Watch Managed AI Agents Run End to End"
        description="Watch the Anter product demo. See how to build a multi-agent system in the visual builder, triage live routing, execute MCP tool calls, and trace every step."
        canonical={VIDEO_WATCH_PATH}
        ogImage={`${BASE_URL}${VIDEO_POSTER}`}
        ogImageAlt={VIDEO_NAME}
        jsonLd={JSON_LD}
      />

      <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text transition-colors duration-300">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 15%, rgba(16,185,129,0.14), transparent 33%), radial-gradient(circle at 80% 30%, rgba(59,130,246,0.12), transparent 38%)",
          }}
        />

        <SiteHeader scrolled={scrolled} />

        {/* pt-28/lg:pt-32 clears the fixed SiteHeader — same offset every other
            interior page uses. The video still lands above the fold. */}
        <main
          id="main-content"
          tabIndex={-1}
          className="relative mx-auto w-full max-w-5xl px-4 pb-24 pt-28 sm:px-5 md:px-8 lg:pt-32"
        >
          <Breadcrumbs items={[{ label: "Product demo" }]} />

          {/* The video, and nothing above it. See the file header before
              inserting anything between this and <main>. */}
          <div className="overflow-hidden rounded-2xl border border-border bg-black shadow-2xl">
            <div className="relative aspect-video">
              <iframe
                src={
                  currentSecond > 0
                    ? `${YOUTUBE_EMBED_URL}?autoplay=1&rel=0&start=${currentSecond}`
                    : `${YOUTUBE_EMBED_URL}?rel=0`
                }
                title={VIDEO_NAME}
                className="h-full w-full border-0"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>

          <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-text sm:text-4xl">
            {VIDEO_NAME}
          </h1>
          <p className="mt-3 max-w-3xl text-base leading-relaxed text-text-muted">
            {VIDEO_DESCRIPTION}
          </p>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.16em] text-text-faint">
            2 min 32 sec · Recorded from the live product · Published{" "}
            <time dateTime={VIDEO_UPLOAD_DATE}>16 July 2026</time>
          </p>

          <section className="mt-10" aria-labelledby="chapters">
            <h2
              id="chapters"
              className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-text-faint"
            >
              Chapters
            </h2>
            <div className="space-y-2">
              {FLOW_EVENTS.map((event, index) => {
                const isActive = index === activeEventIndex;
                return (
                  <button
                    type="button"
                    key={event.second}
                    aria-current={isActive ? "true" : undefined}
                    onClick={() => seekTo(event.second)}
                    className={`group flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition ${
                      isActive
                        ? "border-primary-border bg-primary-dim"
                        : "border-border bg-bg-secondary hover:border-primary-border hover:bg-bg-hover"
                    }`}
                  >
                    <div className="mt-0.5 text-xs font-semibold text-text-faint">
                      {formatClock(event.second)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center text-sm font-semibold text-text">
                        <Dot
                          className={`h-4 w-4 ${isActive ? "text-primary" : "text-text-faint"}`}
                        />
                        {event.title}
                      </div>
                      <p className="mt-0.5 text-xs leading-relaxed text-text-muted">
                        {event.detail}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="mt-10" aria-labelledby="summary">
            <h2 id="summary" className="mb-3 text-xl font-bold tracking-tight text-text">
              What this demo shows
            </h2>
            <div className="space-y-4 text-sm leading-relaxed text-text-muted">
              <p>
                The recording opens in the agent workspace, where a customer-success system is
                assembled from named sub-agents — an intake-triage entry point, plus specialists for
                compliance, billing, refund risk, log investigation, and technical support. The
                routing graph makes the relationships explicit: solid edges are transfers, dashed
                edges are delegations.
              </p>
              <p>
                From there the run moves across product surfaces in a single workflow. Triage picks
                a specialist path, delegation hands leaf work to a sub-agent, and MCP tools and HTTP
                tools are called where the agent needs data it does not hold. Each hop stays
                inspectable — the execution trace records which agent acted, why it routed the way
                it did, and what the tool returned.
              </p>
              <p>
                The walkthrough closes on observability: token usage, model overrides, and execution
                targets shown in the same place the routing decisions were made, so the cost and
                behaviour of an agent are visible without leaving the builder.
              </p>
            </div>
          </section>

          <section className="mt-10 rounded-2xl border border-border bg-bg-secondary/80 p-6">
            <h2 className="text-xl font-bold tracking-tight text-text">
              Build the same thing yourself
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-text-muted">
              Everything in this recording is available in the product — the visual builder, the
              routing graph, MCP tool imports, and the execution traces.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href="/pricing"
                className="inline-flex items-center gap-2 rounded-full border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-primary-text-strong transition hover:opacity-90"
              >
                Get started
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/docs/flows/quickstart"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-bg px-5 py-2.5 text-sm font-semibold text-text transition hover:border-primary-border hover:bg-bg-hover"
              >
                Read the quickstart
              </a>
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
